import { useMemo, useState } from "react";
import { useMastersStore } from "../data/mastersStore";
import type { User } from "../data/schemaTypes";
import { Badge, Button, Card, Input, Select } from "../components/ui";
import { DriverAvatar } from "../features/drivers/DriverAvatar";
import { DriverEditModal } from "../features/drivers/DriverEditModal";
import { CsvUploader } from "../features/imports/CsvUploader";

// Choferes page — list of drivers in the account with their assigned
// vehicle, contact, and status. Editable via modal. The page reads from
// the mastersStore (mock data persisted to localStorage); same shape as
// the future GET /api/drivers response.
export function DriversPage() {
  // Suscripción al array base + memo: usar `selectDrivers` (que hace
  // .filter) como selector de useMastersStore crea un array nuevo cada
  // render → loop infinito en zustand v5. Mantenemos selectDrivers
  // como helper puro para los tests, pero acá filtramos a mano.
  const users = useMastersStore((s) => s.users);
  const warehouses = useMastersStore((s) => s.warehouses);
  const vehicles = useMastersStore((s) => s.vehicles);
  const drivers = useMemo(
    () => users.filter((u) => u.role === "driver"),
    [users],
  );

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [warehouseFilter, setWarehouseFilter] = useState<string>("");
  const [editing, setEditing] = useState<User | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const hydrate = useMastersStore((s) => s.hydrate);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return drivers
      .map((d) => {
        const vehicle = vehicles.find((v) => v.current_driver_user_id === d.id);
        const warehouse = vehicle
          ? warehouses.find((w) => w.id === vehicle.warehouse_id)
          : undefined;
        return { driver: d, vehicle, warehouse };
      })
      .filter(({ driver, warehouse }) => {
        if (
          statusFilter !== "all" &&
          (statusFilter === "active") !== driver.active
        ) {
          return false;
        }
        if (warehouseFilter && warehouse?.id !== warehouseFilter) return false;
        if (!q) return true;
        return (
          driver.full_name.toLowerCase().includes(q) ||
          driver.email.toLowerCase().includes(q) ||
          (driver.phone ?? "").includes(q)
        );
      });
  }, [drivers, vehicles, warehouses, search, statusFilter, warehouseFilter]);

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-ink">Choferes</h1>
          <p className="text-xs text-sub mt-0.5">
            {rows.length} de {drivers.length} choferes ·{" "}
            {drivers.filter((d) => d.active).length} activos
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setUploadOpen(true)}>
            ⬆ Cargar CSV
          </Button>
          <Button variant="primary" size="sm" onClick={() => setCreatingNew(true)}>
            + Nuevo chofer
          </Button>
        </div>
      </header>

      <Card padding="sm" className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <Input
            label="Buscar"
            placeholder="Nombre, email o teléfono…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-44">
          <Select
            label="Almacén"
            value={warehouseFilter}
            onChange={(e) => setWarehouseFilter(e.target.value)}
          >
            <option value="">Todos</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-36">
          <Select
            label="Estado"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as "all" | "active" | "inactive")
            }
          >
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </Select>
        </div>
      </Card>

      <div className="bg-card border border-border rounded-lg shadow-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-page text-sub border-b border-border">
            <tr className="text-left">
              <Th>Chofer</Th>
              <Th>Contacto</Th>
              <Th>Vehículo</Th>
              <Th>Almacén</Th>
              <Th>Estado</Th>
              <th className="px-3 py-2 w-32" />
            </tr>
          </thead>
          <tbody>
            {rows.map(({ driver, vehicle, warehouse }) => (
              <tr
                key={driver.id}
                className="border-b border-border last:border-b-0 hover:bg-page/60"
              >
                <td className="px-3 py-2">
                  <div className="flex items-center gap-3">
                    <DriverAvatar name={driver.full_name} id={driver.id} />
                    <div>
                      <div className="text-ink font-medium">
                        {driver.full_name}
                      </div>
                      <div className="text-[11px] text-sub">{driver.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-sub font-mono text-xs">
                  {driver.phone ?? "—"}
                </td>
                <td className="px-3 py-2 text-ink font-mono text-xs">
                  {vehicle?.plate ?? <span className="text-sub">sin asignar</span>}
                </td>
                <td className="px-3 py-2 text-ink">
                  {warehouse?.name ?? <span className="text-sub">—</span>}
                </td>
                <td className="px-3 py-2">
                  <Badge variant={driver.active ? "delivered" : "pending"}>
                    {driver.active ? "Activo" : "Inactivo"}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => setEditing(driver)}
                    className="text-xs text-primary hover:underline px-1"
                  >
                    Editar
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-sub">
                  No hay choferes que coincidan con los filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <DriverEditModal driver={editing} onClose={() => setEditing(null)} />
      {creatingNew && (
        <DriverEditModal
          driver={null}
          mode="create"
          onClose={() => setCreatingNew(false)}
        />
      )}
      <CsvUploader
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        title="Cargar choferes desde CSV"
        endpoint="/api/users/bulk"
        description={
          <>
            <strong>Requeridas:</strong> <code>email</code>, <code>full_name</code>.{" "}
            <strong>Opcionales:</strong> <code>phone</code>, <code>code</code>,{" "}
            <code>vehicle_type</code> (FURGON-25 / FURGON-30 / CAMION-100),{" "}
            <code>plate</code> (auto si vacío). Si viene <code>vehicle_type</code>{" "}
            se crea/asigna un vehículo. Upsert por email.
          </>
        }
        columns={[
          { key: "email", aliases: ["email", "correo"], required: true },
          { key: "full_name", aliases: ["full_name", "nombre", "nombre completo"], required: true },
          { key: "phone", aliases: ["phone", "telefono", "teléfono"] },
          { key: "code", aliases: ["code", "codigo", "código", "transportista"] },
          { key: "vehicle_type", aliases: ["vehicle_type", "tipo_vehiculo", "tipo vehiculo", "tipo"] },
          { key: "plate", aliases: ["plate", "placa", "patente"] },
        ]}
        onSuccess={() => hydrate()}
      />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wide">
      {children}
    </th>
  );
}
